/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { F, Icon } from 'nallian-shared-ui/lib/components';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { change } from 'redux-form';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import principalsCompleteService from '../services/PrincipalsCompleteService';

const Wrapper = styled.div`
  & {
    :focus-within {
      .ui.input input,
      .ui.input input:focus,
      .ui.selection.dropdown {
        border-color: #85b7d9;
        color: rgba(0, 0, 0, 0.95);
      }
    }
    display: flex;
    flex-flow: row;
    width: 100%;
    div:first-child {
      width: unset;
      input {
        min-width: 9rem;
      }
    }
    .field {
      flex-grow: 1;
    }
  }
`;

// Handle search shows all records as received. We need this func otherwise the component filters the results
const handleSearch = r => r;

const PrincipalsDropdown = ({
  disabled,
  dispatchPrincipalsCompleteGet,
  principalsCompleteFetch,
  input,
  dispatchChange,
  meta,
  organisationId,
  hideLabel,
  onChange,
  clearAfterChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedDispatch = useDebouncedCallback(dispatchPrincipalsCompleteGet, 400, {
    leading: true,
  });

  const principalsCompleteSelectOptions = useMemo(
    () =>
      !searchQuery && clearAfterChange
        ? undefined
        : (principalsCompleteFetch.value || []).map(p => ({
            key: p.id,
            value: p.id,
            text: `${p.fullName} - (${p.primaryEmailAddress || p.primaryPhoneNumber})`,
          })),
    [searchQuery, clearAfterChange, principalsCompleteFetch.value]
  );

  useEffect(() => {
    if (searchQuery.length >= 2) debouncedDispatch({ fullName: searchQuery, organisationId }, t);
  }, [searchQuery, debouncedDispatch, organisationId, t]);

  const handleSearchChange = useCallback((_, { searchQuery: query }) => setSearchQuery(query), [
    setSearchQuery,
  ]);

  const handleSearchFocus = useCallback(() => setSearchQuery(''), [setSearchQuery]);

  const handleChange = useCallback(
    (_, data) => {
      setSearchQuery('');
      if (onChange) onChange(_, data);
      else if (dispatchChange && meta?.form) dispatchChange(meta.form, 'principalId', data.value);
    },
    [setSearchQuery, meta, dispatchChange, onChange]
  );

  return (
    <Wrapper>
      <div className="ui field">
        {!hideLabel && <label htmlFor="principalId">{t('selectExistingUser')}</label>}
        <F.Select
          id="principalId"
          labelPosition="left"
          value={!searchQuery && clearAfterChange ? undefined : input?.value}
          onChange={handleChange}
          name={input?.name}
          options={principalsCompleteSelectOptions}
          onSearchChange={handleSearchChange}
          onFocus={handleSearchFocus}
          searchQuery={searchQuery}
          loading={principalsCompleteFetch.pending}
          noResultsMessage={!principalsCompleteFetch.pending ? t('noUsersFound') : ''}
          icon={<Icon name="search" size="small" margin="-0.45rem -0.75rem" />}
          disabled={disabled}
          minCharacters={2}
          fluid
          clearable
          selectOnNavigation={false}
          selectOnBlur={false}
          search={handleSearch}
          lazyLoad
          {...props}
        />
      </div>
    </Wrapper>
  );
};

PrincipalsDropdown.propTypes = {
  principalsCompleteSelectOptions: PropTypes.array.isRequired,
  principalsCompleteFetch: PropTypes.object.isRequired,
  dispatchPrincipalsCompleteGet: PropTypes.func.isRequired,
  organisationId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  input: PropTypes.object.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  hideLabel: PropTypes.bool,
  clearAfterChange: PropTypes.bool,
  onChange: PropTypes.func,
};

PrincipalsDropdown.defaultProps = {
  clearAfterChange: false,
  disabled: false,
  hideLabel: false,
  onChange: undefined,
};

export default compose(
  connect(null, { dispatchChange: change }),
  principalsCompleteService
)(PrincipalsDropdown);
